//NPM LIBRARIES
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form } from 'reactstrap';
import { Redirect } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import axios from 'axios';

//REDUX ACTIONS AND SELECTORS
import { addPageAlert } from '../../PortalStore/Actions/PageAlertActions';
import { getFormPropertiesSelector, getFormServiceStatusSelector, getFormDataSelector } from '../../FormStore/Selectors/FormDataSelectors';
import { addFormProperties, addFormDraftProperties } from '../../FormStore/Actions/FormPropertyAction/FormPropertyAction';
import { loadDraft, LOAD_DRAFT_SUCCESS } from '../../FormStore/Actions/FormServiceActions/GenericFormServiceActions/LoadDraftAction';
import { verifyUserRole, VERIFY_USER_ROLE_SUCCESS } from '../../FormStore/Actions/FormServiceActions/IndustryAssist/VerifyUserRoleAction'
import { addDraftFormDataPromise } from '../../FormStore/Actions/FormDataActions/GenericFormDataAction';
import { processUrlParameters } from './Utilities'

//COMPONENTS AND UTILITIES
import Heading from '../CASA/Heading';
import CASASpinner from '../Portal/CASASpinner';

class Application extends PureComponent {
    constructor(props) {
        super(props);
        let urlParameters = null;
        if (window.location.search) {
            urlParameters = processUrlParameters(window.location.search, this.props.dispatch);
        }

        let breadcrumbs = [
            {
                link: urlParameters?.organisationId ? '/dashorg?casaorgid=' + urlParameters.organisationId : '/dashind',
                text: 'myCASA'
            },
            {
                text: props.formInformation.title ?? props.formInformation.name
            }];

        if (this.props.breadcrumbs) {
            breadcrumbs = this.props.breadcrumbs;
        }

        let apploaded = false;
        let orgid = props.formInformation.organisationId ?? urlParameters?.organisationId ?? null;

        if (props.formProperties.name !== props.formInformation.name) {
            props.dispatch(
                addFormProperties(
                    {
                        'name': props.formInformation.name,
                        'formCode': props.formInformation.formCode,
                        'rootFormCode': props.formInformation.rootFormCode,
                        'formDataType': props.formInformation.formDataType,
                        'version': props.formInformation.version,
                        'organisationArn': props.formInformation.organisationArn ?? urlParameters?.organisationArn ?? null,
                        'orgid': orgid,
                        'draftReturnUrl': this.getSaveExitPortalUrl(orgid, breadcrumbs)
                    }
                )
            );
        }

        this.state = {
            applicationLoaded: apploaded,
            breadcrumbs: breadcrumbs,
            savingApplication: false,
            organisationArn: props.formInformation.organisationArn ?? urlParameters?.organisationArn ?? null,
            recordId: urlParameters?.recordid ?? null,
            userVerified: false
        }
    }

    async componentDidMount() {
        let verifiedUser = true;

        //Verify roles
        if (this.props.allowedRoles && this.state.organisationArn) {
            verifiedUser = await this.verifyUserRole(this.state.organisationArn, this.props.allowedRoles);
        }

        if (verifiedUser) {
            this.setState({ userVerified: true })
            this.loadDraftForm();
        } else {
            this.setState({ userVerified: false, applicationLoaded: true })
        }
    }

    verifyUserRole = (organisationArn, allowedRoles) => {
        const webappurlForms = this.props.B2CSettings.items.webappurlFormsApi;
        return this.props.dispatch(verifyUserRole(webappurlForms, this.props.B2CSettings.items.accesstokenForms, organisationArn, allowedRoles))
            .then(response => {
                if (response.type === VERIFY_USER_ROLE_SUCCESS) {
                    return true
                } else {
                    this.props.dispatch(addPageAlert({
                        level: "error",
                        title: "Unable to complete application online",
                        message: "Unfortunately you don't have the correct role to complete this application"
                    }));
                    return false
                }
            })
            .catch(() => {
                this.props.dispatch(addPageAlert({
                    level: "error",
                    title: "Unable to complete application online",
                    message: "Unfortunately you don't have the correct role to complete this application"
                }));
                return false
            });
    }

    loadDraftForm = () => {
        if (!this.state.recordId) {
            this.setState({ applicationLoaded: true });
            return
        }

        const webappurlForms = this.props.B2CSettings.items.webappurlFormsApi;
        this.props.dispatch(loadDraft(webappurlForms, this.props.B2CSettings.items.accesstokenForms, this.state.recordId, true))
            .then(response => {
                if (response.type === LOAD_DRAFT_SUCCESS) {
                    const draftFormData = JSON.parse(JSON.parse(response.payload)?.formData).formData;
                    this.setState({ applicationLoaded: false });

                    //Add the data to either a custom data location or the generic formData
                    if (this.props.formActions?.loadDraft) {
                        this.props.formActions.loadDraft(draftFormData)
                        this.setState({ applicationLoaded: true })
                    } else {
                        axios.all([
                            this.props.dispatch(addDraftFormDataPromise(draftFormData))
                        ])
                            .then(() => {
                                this.setState({ applicationLoaded: true });
                            });
                    }
                }
                else {
                    this.setState({ applicationLoaded: true });
                    this.props.dispatch(addPageAlert({
                        level: "error",
                        title: "We're sorry. Something has gone wrong. Try again. If the error persists contact the CASA Help Desk.",
                        message: "System error ALD.1"
                    }));
                }
            });
    }

    getSaveExitPortalUrl = (orgid, breadcrumbs) => {
        let portalReturnUrl = "";

        if (breadcrumbs && breadcrumbs[1].link) {
            return breadcrumbs[1].link;
        }
        if (breadcrumbs && breadcrumbs[0].link) {
            return breadcrumbs[0].link;
        }

        //window.location.host begins with localhost
        if (this.props.envdata.items.systemsettings) {
            let portalSetting = this.props.envdata.items.systemsettings.value.find(systemSetting => systemSetting.casa_name === 'Portal - Home URL');
            if (orgid !== null) {
                portalReturnUrl = portalSetting.casa_stringvalue + '/dashorg/?casaorgid=' + orgid + '&saveFormSuccess=1';
            } else {
                portalReturnUrl = portalSetting.casa_stringvalue + '/dashind/?saveFormSuccess=1';
            }
        }

        return portalReturnUrl;
    }

    render() {
        if (this.state.applicationLoadedError) {
            return <Redirect to={'/systemerror?message=' + this.state.applicationLoadedErrorMsg} />
        }

        if (!this.state.applicationLoaded) {
            return <CASASpinner />
        }

        return (
            <Fragment>
                <MetaTags>
                    <title>{this.props.formInformation.name}</title>
                </MetaTags>

                {this.state.savingApplication ? <CASASpinner message='Saving form' /> : null}

                <Heading
                    id="pageheading"
                    text={this.props.formInformation.title ?? this.props.formInformation.name}
                    breadcrumbs={this.state.breadcrumbs}
                    showcrumbs={this.state.showItems}
                    showback={this.props.enableForm ? this.props.showback : true} />

                {this.props.enableForm && this.state.userVerified &&
                    <Form
                        className="advanced-form">

                        {this.props.children}

                    </Form>}
            </Fragment>
        );
    }
}

Application.defaultProps = {
    enableForm: true,
    enableSaveButton: true
}

Application.propTypes = {
    formInformation: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        title: PropTypes.string,
        rootFormCode: PropTypes.string.isRequired,
        formCode: PropTypes.string.isRequired,
        formDataType: PropTypes.string,
        version: PropTypes.string.isRequired,
        organisationId: PropTypes.string,
        organisationArn: PropTypes.string
    }),
    formActions: PropTypes.shape({
        saveDraft: PropTypes.func,
        loadDraft: PropTypes.func
    }),
    dispatch: PropTypes.func,
    envdata: PropTypes.object,
    formProperties: PropTypes.object,
    B2CSettings: PropTypes.object,
    breadcrumbs: PropTypes.array,
    showback: PropTypes.bool,
    successMessage: PropTypes.func,
    initialStep: PropTypes.string,
    submissionStatus: PropTypes.object,
    loadDraftStatus: PropTypes.object,
    formData: PropTypes.object,
    allowedRoles: PropTypes.array,
    enableForm: PropTypes.bool,
    enableSaveButton: PropTypes.bool
};

const mapStateToProps = (state, props) => {
    return {
        envdata: state.envdata,
        formProperties: getFormPropertiesSelector(state.formStore),
        formData: getFormDataSelector(state.formStore, props.formInformation.formDataType),
        B2CSettings: state.B2CSettings,
        loadDraftStatus: getFormServiceStatusSelector(state.formStore, 'loadDraft'),
        saveDraftStatus: getFormServiceStatusSelector(state.formStore, 'saveDraft')
    }
}

export default connect(mapStateToProps)(Application);